import React, { useState, useRef, useEffect } from 'react';

// Gráfico de linha (SVG) com área em degradê, grade e tooltip — usado tanto na
// tela de evolução física da aluna quanto na do admin, pra manter os dois
// visualmente consistentes.
export default function EvolucaoLineChart({ dados, indicador, cor, unidade }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 240 });

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      for (const e of entries) setDimensions({ width: e.contentRect.width || 600, height: 240 });
    });
    if (svgRef.current?.parentElement) obs.observe(svgRef.current.parentElement);
    return () => obs.disconnect();
  }, []);

  const valores = dados.map(d => parseFloat(d[indicador])).filter(v => !isNaN(v));
  if (valores.length < 2) return (
    <div style={{ textAlign:'center', padding:'32px', color:'#8B7355', fontSize:'13px' }}>
      {valores.length === 0 ? 'Sem dados para este indicador' : 'Adicione mais registros para ver o gráfico'}
    </div>
  );

  const { width, height } = dimensions;
  const PAD = { top: 20, right: 24, bottom: 48, left: 52 };
  const W = width - PAD.left - PAD.right;
  const H = height - PAD.top - PAD.bottom;
  const min = Math.min(...valores), max = Math.max(...valores);
  const range = max - min || 1;
  const minY = min - range * 0.15, maxY = max + range * 0.15;

  const pontos = dados.map((d, i) => {
    const v = parseFloat(d[indicador]);
    if (isNaN(v)) return null;
    const x = PAD.left + (i / (dados.length - 1 || 1)) * W;
    const y = PAD.top + H - ((v - minY) / (maxY - minY)) * H;
    return { x, y, v, d };
  }).filter(Boolean);

  const pathD = pontos.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = pathD + ` L ${pontos[pontos.length-1].x} ${PAD.top+H} L ${pontos[0].x} ${PAD.top+H} Z`;
  const gridLines = Array.from({ length: 5 }, (_, i) => {
    const frac = i / 4, val = minY + frac * (maxY - minY), y = PAD.top + H - frac * H;
    return { y, val };
  });
  const step = Math.ceil(pontos.length / Math.min(pontos.length, 8));
  const xLabels = pontos.filter((_, i) => i % step === 0 || i === pontos.length - 1);
  const fmtDate = str => new Date(str + 'T12:00:00').toLocaleDateString('pt-BR', { month:'short', day:'numeric' });
  const gradId = `grad-${indicador}-${cor.replace('#', '')}`;

  return (
    <div style={{ position:'relative', width:'100%' }}>
      <svg ref={svgRef} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow:'visible' }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={cor} stopOpacity="0.18" />
            <stop offset="100%" stopColor={cor} stopOpacity="0.01" />
          </linearGradient>
        </defs>
        {gridLines.map((g, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={g.y} x2={PAD.left+W} y2={g.y} stroke="#E3DBC7" strokeWidth="1" strokeDasharray="4 4" />
            <text x={PAD.left-8} y={g.y+4} textAnchor="end" fontSize="11" fill="#8B7355" fontFamily="Inter, sans-serif">{g.val.toFixed(1)}</text>
          </g>
        ))}
        <path d={areaD} fill={`url(#${gradId})`} />
        <path d={pathD} fill="none" stroke={cor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {xLabels.map((p, i) => <text key={i} x={p.x} y={PAD.top+H+20} textAnchor="middle" fontSize="11" fill="#8B7355" fontFamily="Inter, sans-serif">{fmtDate(p.d.data)}</text>)}
        {pontos.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="14" fill="transparent" style={{ cursor:'pointer' }} onMouseEnter={() => setTooltip({...p,i})} onMouseLeave={() => setTooltip(null)} />
            <circle cx={p.x} cy={p.y} r="4" fill="white" stroke={cor} strokeWidth="2.5" style={{ pointerEvents:'none' }} />
          </g>
        ))}
        {tooltip && (() => {
          const tx = Math.min(Math.max(tooltip.x, PAD.left+40), width-80);
          const ty = tooltip.y > PAD.top+60 ? tooltip.y-44 : tooltip.y+14;
          return (
            <g>
              <rect x={tx-42} y={ty} width="84" height="32" rx="6" fill="#251C19" fillOpacity="0.88" />
              <text x={tx} y={ty+13} textAnchor="middle" fontSize="11" fill="#FAF7F2" fontFamily="Inter, sans-serif">{fmtDate(tooltip.d.data)}</text>
              <text x={tx} y={ty+26} textAnchor="middle" fontSize="12" fill={cor} fontFamily="Inter, sans-serif" fontWeight="600">{tooltip.v.toFixed(1)}{unidade}</text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
